import { observer } from 'mobx-react-lite';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../../global-state/themeContext';
import { SortDirection } from '../../../global-state/types';
import { CustomerExperienceViewItem, CustomerExperienceViewResponse } from '../../../hooks';
import { useFilteredDataByArea } from '../../../hooks/useFilteredData';
import { Typography } from '../../../stories/atoms';
import { ColumnFlexContainer } from '../../../stories/atoms/containerV2/ContainerV2';
import { Animation, Animations } from '../../../stories/molecules';
import { HeaderRowItem, Table } from '../../../stories/molecules/tablev2/Table';
import { muiTheme } from '../../../theme';
import { commonTranslations, homeTranslations } from '../../../translations';
import { areaResultTranslations } from '../../../translations/areaResult';
import { formatAmountWithSeparator } from '../../../utils';
import { AreaResultHeaderTooltip, SummaryType } from '../areaResultHeaderTooltip/AreaResultHeaderTooltip';

export type CustomerExperienceViewProps = {
  isLoading: boolean;
  isError: boolean;
  storesByArea: string[];
  customerExperienceData: CustomerExperienceViewResponse | null;
  selectedStores: string[];
};

enum CustomerExperienceColumnType {
  StoreId = 'storeId',
  CustomerSatisfaction = 'customerSatisfaction',
  NegDiff = 'negDiff',
  ApprovalRate = 'approvalRate',
  RetaggedItems = 'retaggedItems',
  AddedSales = 'addedSales',
  NoOfScanners = 'noOfScanners',
  HoursSpent = 'hoursSpent',
  QueueLength = 'queueLength',
  OrisCR = 'orisCR',
}

export const CustomerExperienceView: FC<CustomerExperienceViewProps> = observer(
  ({ isLoading, isError, storesByArea, customerExperienceData, selectedStores }) => {
    const { t } = useTranslation();
    const { customerExperience } = areaResultTranslations;
    const { mode } = useThemeContext();

    const isDarkMode = mode === 'dark';
    const themeBorderColor = isDarkMode ? muiTheme.color.borderGrayDark : muiTheme.color.borderGray;
    const headerBorderBottomColor = isDarkMode ? muiTheme.color.primary : muiTheme.color.borderGray;
    const [sortColumnId, setSortColumnId] = useState<CustomerExperienceColumnType | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection | null>(null);

    const headerFooterSxProps = {
      padding: '12px 8px',
      ':nth-of-type(2),:nth-of-type(8),:nth-of-type(9)': {
        border: `1px solid ${themeBorderColor}`,
        borderLeft: 'none',
        borderTop: 'none',
        borderBottom: `1px solid ${headerBorderBottomColor}`,
      },
    };

    const filteredCustomerExperienceData = useFilteredDataByArea(
      customerExperienceData?.customerExperience,
      storesByArea,
      selectedStores,
    );

    const headerItems: HeaderRowItem<CustomerExperienceColumnType>[] = [
      {
        id: CustomerExperienceColumnType.StoreId,
        content: (
          <Typography>
            {t(areaResultTranslations.storeId.key, areaResultTranslations.storeId.defaultValue)}
          </Typography>
        ),
      },
      {
        id: CustomerExperienceColumnType.CustomerSatisfaction,
        content: (
          <AreaResultHeaderTooltip
            headerText="CSAT"
            isBold
            tooltipText={t(
              homeTranslations.customerFeedback.toolTip.key,
              homeTranslations.customerFeedback.toolTip.defaultValue,
            )}
            summaryType={SummaryType.GROSSAVG}
          />
        ),
      },
      {
        id: CustomerExperienceColumnType.NegDiff,
        content: (
          <ColumnFlexContainer>
            <Typography type="button">RFID</Typography>
            <AreaResultHeaderTooltip
              headerText={t(customerExperience.negDiff.key, customerExperience.negDiff.defaultValue)}
              tooltipText={t(
                homeTranslations.stockAccuracy.negativeDifferenceText.key,
                homeTranslations.stockAccuracy.negativeDifferenceText.defaultValue,
              )}
              summaryType={SummaryType.GROSSAVG}
            />
          </ColumnFlexContainer>
        ),
      },
      {
        id: CustomerExperienceColumnType.ApprovalRate,
        content: (
          <AreaResultHeaderTooltip
            headerText={t(customerExperience.approvalRate.key, customerExperience.approvalRate.defaultValue)}
            tooltipText={t(
              homeTranslations.stockAccuracy.negativeApprovalRateTooltip.key,
              homeTranslations.stockAccuracy.negativeApprovalRateTooltip.defaultValue,
            )}
            summaryType={SummaryType.GROSSAVG}
          />
        ),
      },
      {
        id: CustomerExperienceColumnType.RetaggedItems,
        content: (
          <AreaResultHeaderTooltip
            headerText={t(customerExperience.retaggedItems.key, customerExperience.retaggedItems.defaultValue)}
            tooltipText={t(
              homeTranslations.rfidSalesRetagging.retaggingRateDescription.key,
              homeTranslations.rfidSalesRetagging.retaggingRateDescription.defaultValue,
            )}
            summaryType={SummaryType.GROSSAVG}
          />
        ),
      },
      {
        id: CustomerExperienceColumnType.AddedSales,
        content: (
          <AreaResultHeaderTooltip
            headerText={t(customerExperience.addedSales.key, customerExperience.addedSales.defaultValue)}
            tooltipText={t(
              homeTranslations.rfidSalesRetagging.addedSalesDescription.key,
              homeTranslations.rfidSalesRetagging.addedSalesDescription.defaultValue,
            )}
            summaryType={SummaryType.GROSSAVG}
          />
        ),
      },
      {
        id: CustomerExperienceColumnType.NoOfScanners,
        content: (
          <AreaResultHeaderTooltip
            headerText={t(customerExperience.noOfScanners.key, customerExperience.noOfScanners.defaultValue)}
            tooltipText={t(
              homeTranslations.rfidCount.scannerText.tooltip.key,
              homeTranslations.rfidCount.scannerText.tooltip.defaultValue,
            )}
            summaryType={SummaryType.SUM}
          />
        ),
      },
      {
        id: CustomerExperienceColumnType.HoursSpent,
        content: (
          <AreaResultHeaderTooltip
            headerText={t(customerExperience.hoursSpent.key, customerExperience.hoursSpent.defaultValue)}
            tooltipText={t(
              homeTranslations.rfidCount.timeText.subTitle.key,
              homeTranslations.rfidCount.timeText.subTitle.defaultValue,
            )}
            summaryType={SummaryType.SUM}
          />
        ),
      },
      {
        id: CustomerExperienceColumnType.QueueLength,
        content: (
          <AreaResultHeaderTooltip
            headerText={t(customerExperience.queueLength.key, customerExperience.queueLength.defaultValue)}
            tooltipText={t(
              homeTranslations.queueLength.toolTip.key,
              homeTranslations.queueLength.toolTip.defaultValue,
            )}
            summaryType={SummaryType.GROSSAVG}
            isBold
          />
        ),
      },
      {
        id: CustomerExperienceColumnType.OrisCR,
        content: (
          <AreaResultHeaderTooltip
            headerText="ORIS CR"
            tooltipText={t(
              homeTranslations.orisCustomers.orisConversionRate.tooltip.key,
              homeTranslations.orisCustomers.orisConversionRate.tooltip.defaultValue,
            )}
            summaryType={SummaryType.GROSSAVG}
            isBold
          />
        ),
      },
    ];

    const isNoData = useMemo(() => {
      return !customerExperienceData || !filteredCustomerExperienceData?.length;
    }, [customerExperienceData, filteredCustomerExperienceData, storesByArea]);

    const getTableItem = (item: CustomerExperienceViewItem, isSummary: boolean) => {
      const typographyType = isSummary ? 'body3' : 'body2';
      return [
        {
          id: CustomerExperienceColumnType.StoreId,
          width: '10%',
          content: (
            <Typography type={typographyType}>
              {isSummary
                ? t(areaResultTranslations.summary.key, areaResultTranslations.summary.defaultValue)
                : item.storeId}
            </Typography>
          ),
        },
        {
          id: CustomerExperienceColumnType.CustomerSatisfaction,
          content: (
            <Typography type={typographyType}>{formatAmountWithSeparator(item.customerSatisfaction)}</Typography>
          ),
        },
        {
          id: CustomerExperienceColumnType.NegDiff,
          content: <Typography type={typographyType}>{formatAmountWithSeparator(item.negDiff, '%')}</Typography>,
        },
        {
          id: CustomerExperienceColumnType.ApprovalRate,
          content: (
            <Typography type={typographyType}>{formatAmountWithSeparator(item.approvalRate, '%')}</Typography>
          ),
        },

        {
          id: CustomerExperienceColumnType.RetaggedItems,
          content: (
            <Typography type={typographyType}>{formatAmountWithSeparator(item.retaggingRate, '%')}</Typography>
          ),
        },
        {
          id: CustomerExperienceColumnType.AddedSales,
          content: (
            <Typography type={typographyType}>{formatAmountWithSeparator(item.addedSales, '%')}</Typography>
          ),
        },
        {
          id: CustomerExperienceColumnType.NoOfScanners,
          content: <Typography type={typographyType}>{formatAmountWithSeparator(item.noOfScanners)}</Typography>,
        },
        {
          id: CustomerExperienceColumnType.HoursSpent,
          content: <Typography type={typographyType}>{formatAmountWithSeparator(item.hoursSpent)}</Typography>,
        },
        {
          id: CustomerExperienceColumnType.QueueLength,
          content: (
            <Typography type={typographyType}>{formatAmountWithSeparator(item.queueLength, '%')}</Typography>
          ),
        },
        {
          id: CustomerExperienceColumnType.OrisCR,
          content: <Typography type={typographyType}>{formatAmountWithSeparator(item.orisCR, '%')}</Typography>,
        },
      ];
    };

    const getFooterItems = () => {
      if (selectedStores.length !== 0 || !customerExperienceData?.summary) {
        return [];
      }
      return getTableItem({ storeId: 'Summary', ...customerExperienceData?.summary }, true);
    };

    const onColumnSort = (columnType: CustomerExperienceColumnType) => {
      if (columnType !== sortColumnId) {
        setSortColumnId(columnType);
        setSortDirection(SortDirection.Descending);
        return;
      }

      if (sortDirection === SortDirection.Ascending) {
        setSortColumnId(columnType);
        setSortDirection(SortDirection.Descending);
        return;
      }

      setSortColumnId(columnType);
      setSortDirection(SortDirection.Ascending);
    };

    return (
      <>
        {isLoading && (
          <ColumnFlexContainer flex={1} data-testid="customer-experience-loading-view">
            <Animation
              title={t(customerExperience.loading.key, customerExperience.loading.defaultValue)}
              animation={Animations.LOADING}
            />
          </ColumnFlexContainer>
        )}

        {!isLoading && isError && (
          <ColumnFlexContainer flex={1} data-testid="customer-experience-error-view">
            <Animation
              title={t(commonTranslations.errorText1.key, commonTranslations.errorText1.defaultValue)}
              subtitle={t(commonTranslations.errorText2.key, commonTranslations.errorText2.defaultValue)}
              animation={Animations.SAD}
            />
          </ColumnFlexContainer>
        )}

        {!isLoading && !isError && isNoData && (
          <ColumnFlexContainer flex={1} data-testid="customer-experience-no-data-view">
            <Animation
              title={t(customerExperience.noData.key, customerExperience.noData.defaultValue)}
              animation={Animations.CONFUSED}
            />
          </ColumnFlexContainer>
        )}

        {!isLoading && !isError && !isNoData && (
          <ColumnFlexContainer flex={1} padding={[4, 0]} minHeight={[0]} data-testid="customer-experience-table">
            <Table
              items={filteredCustomerExperienceData}
              headerItems={headerItems}
              stickyHeader
              stickyFirstColumn
              disableHover
              sortConfig={{
                storeId: { getItemValue: (item) => item.storeId },
                customerSatisfaction: { getItemValue: (item) => item.customerSatisfaction },
                negDiff: { getItemValue: (item) => item.negDiff },
                approvalRate: { getItemValue: (item) => item.approvalRate },
                retaggedItems: { getItemValue: (item) => item.retaggingRate },
                addedSales: { getItemValue: (item) => item.addedSales },
                noOfScanners: { getItemValue: (item) => item.noOfScanners },
                hoursSpent: { getItemValue: (item) => item.hoursSpent },
                queueLength: { getItemValue: (item) => item.queueLength },
                orisCR: { getItemValue: (item) => item.orisCR },
              }}
              sortColumnId={sortColumnId || undefined}
              onSort={onColumnSort}
              sortDirection={sortDirection || undefined}
              sxProps={{
                padding: '12px 8px',
                ':nth-of-type(2),:nth-of-type(8),:nth-of-type(9)': {
                  border: `1px solid ${themeBorderColor}`,
                  borderLeft: 'none',
                  borderTop: 'none',
                },
                ':nth-of-type(3),:nth-of-type(4),:nth-of-type(5),:nth-of-type(6),:nth-of-type(7)': {
                  border: `1px dashed ${themeBorderColor}`,
                  borderLeft: 'none',
                  borderTop: 'none',
                  borderBottom: `1px solid ${themeBorderColor}`,
                },
              }}
              headerSxProps={headerFooterSxProps}
              footerSxProps={headerFooterSxProps}
              getRowItems={(item) => {
                return getTableItem(item, false);
              }}
              footerItems={getFooterItems()}
            />
          </ColumnFlexContainer>
        )}
      </>
    );
  },
);
